import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { CountrySelect } from '../ui/country-select.jsx';
import { PickOnMapButton } from '../map/pick-on-map-button.jsx';
import { ApiError, api } from '../../api/index.js';

const SIDC_RE = /^\d{20}$/;

// Default SIDC-20 for OSS units (Friendly Infantry Battalion, no HQ flag).
// Operators can override per-unit via the SIDC-20 field below.
const DEFAULT_UNIT_SIDC = '10031000161211000000';

const EMPTY = {
  name: '', shortName: '', description: '',
  countryCode: '', latitude: '', longitude: '',
  homeNodeId: '',
  sidc20: DEFAULT_UNIT_SIDC,
};

function parseCoord(s) {
  if (s == null || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

export function UnitFormModal({ open, mode, parentName, initial, onClose, onSave }) {
  const isCreate = mode === 'create';
  const [values, setValues] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [nodes, setNodes] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    api.nodes.list().then((r) => setNodes(r.nodes || [])).catch(() => setNodes([]));
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setValues(
      isCreate
        ? { ...EMPTY }
        : {
            name: initial?.name || '',
            shortName: initial?.shortName || '',
            description: initial?.description || '',
            countryCode: initial?.countryCode || '',
            latitude: initial?.latitude != null ? String(initial.latitude) : '',
            longitude: initial?.longitude != null ? String(initial.longitude) : '',
            homeNodeId: initial?.homeNodeId != null ? String(initial.homeNodeId) : '',
            sidc20: initial?.sidc20 || DEFAULT_UNIT_SIDC,
          },
    );
  }, [open, isCreate, initial]);

  const errors = useMemo(() => {
    const e = {};
    if (!values.name.trim()) e.name = 'Name is required.';
    if (values.name.length > 128) e.name = 'Max 128 characters.';
    if (values.shortName && values.shortName.length > 32) e.shortName = 'Max 32 characters.';
    if (values.latitude !== '') {
      const n = parseCoord(values.latitude);
      if (Number.isNaN(n) || n < -90 || n > 90) e.latitude = 'Latitude must be in [-90, 90].';
    }
    if (values.longitude !== '') {
      const n = parseCoord(values.longitude);
      if (Number.isNaN(n) || n < -180 || n > 180) e.longitude = 'Longitude must be in [-180, 180].';
    }
    if ((values.latitude === '') !== (values.longitude === '')) {
      if (values.latitude === '') e.latitude = 'Both coordinates required.';
      if (values.longitude === '') e.longitude = 'Both coordinates required.';
    }
    if (values.sidc20 && !SIDC_RE.test(values.sidc20)) {
      e.sidc20 = 'SIDC-20 must be exactly 20 digits.';
    }
    return e;
  }, [values]);

  const canSubmit = Object.keys(errors).length === 0 && !busy;

  function update(field, v) {
    setValues((prev) => ({ ...prev, [field]: v }));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        name: values.name.trim(),
        shortName: values.shortName.trim() || null,
        description: values.description.trim() || null,
        countryCode: values.countryCode || null,
        latitude: values.latitude === '' ? null : Number(values.latitude),
        longitude: values.longitude === '' ? null : Number(values.longitude),
        homeNodeId: values.homeNodeId ? Number(values.homeNodeId) : null,
        sidc20: values.sidc20 ? values.sidc20.trim() : null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={isCreate ? (parentName ? `Create sub-unit under "${parentName}"` : 'Create root unit') : `Edit ${initial?.name || 'unit'}`}
      description={
        isCreate
          ? 'A unit is a tactical or organizational element. Add personnel after creation.'
          : 'Edit metadata. Inheritance: country, coords, and anchor are inherited from the nearest parent that has them set.'
      }
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className={`fa-solid ${isCreate ? 'fa-plus' : 'fa-floppy-disk'}`} aria-hidden="true" />
                {isCreate ? 'Create unit' : 'Save changes'}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label htmlFor="unit-name" className="field-label">Name</label>
            <input
              id="unit-name"
              className="field"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              autoComplete="off"
              disabled={busy}
              maxLength={128}
            />
            {errors.name && <p className="mt-1 text-xs text-rose-600">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="unit-short" className="field-label">
              Short name <span className="font-normal text-mist">(optional)</span>
            </label>
            <input
              id="unit-short"
              className="field font-mono text-[13px]"
              value={values.shortName}
              onChange={(e) => update('shortName', e.target.value)}
              autoComplete="off"
              disabled={busy}
              maxLength={32}
            />
            {errors.shortName && <p className="mt-1 text-xs text-rose-600">{errors.shortName}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="unit-country" className="field-label">
              Country <span className="font-normal text-mist">(optional)</span>
            </label>
            <CountrySelect
              id="unit-country"
              value={values.countryCode || null}
              onChange={(v) => update('countryCode', v || '')}
              disabled={busy}
            />
          </div>

          {/* Coordinates with Pick-on-map button — same layout as NodeFormModal. */}
          <div className="sm:col-span-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="field-label !mb-0">Coordinates (°)</span>
              <PickOnMapButton
                latitude={values.latitude}
                longitude={values.longitude}
                title={values.name ? `Pick location for ${values.name}` : 'Pick unit location'}
                disabled={busy}
                onChange={(lat, lon) => {
                  setValues((p) => ({
                    ...p,
                    latitude:  lat.toFixed(6),
                    longitude: lon.toFixed(6),
                  }));
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  id="unit-lat"
                  type="number"
                  step="any"
                  min="-90"
                  max="90"
                  className="field font-mono text-[13px]"
                  placeholder="Latitude  e.g. 49.5500"
                  value={values.latitude}
                  onChange={(e) => update('latitude', e.target.value)}
                  disabled={busy}
                />
                {errors.latitude && <p className="mt-1 text-xs text-rose-600">{errors.latitude}</p>}
              </div>
              <div>
                <input
                  id="unit-lon"
                  type="number"
                  step="any"
                  min="-180"
                  max="180"
                  className="field font-mono text-[13px]"
                  placeholder="Longitude  e.g. 25.5900"
                  value={values.longitude}
                  onChange={(e) => update('longitude', e.target.value)}
                  disabled={busy}
                />
                {errors.longitude && <p className="mt-1 text-xs text-rose-600">{errors.longitude}</p>}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="unit-home" className="field-label">
            Home node <span className="font-normal text-mist">(default routing — soft preference)</span>
          </label>
          <select
            id="unit-home"
            className="field"
            value={values.homeNodeId}
            onChange={(e) => update('homeNodeId', e.target.value)}
            disabled={busy}
          >
            <option value="">— inherit from parent —</option>
            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name} · {n.tier} · {n.status}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-mist">
            Personnel default to this home for terminal connections. They can still talk to other nodes when in range —
            unless the person or channel has <em>Home node only</em> set.
          </p>
        </div>

        <div>
          <label htmlFor="unit-sidc" className="field-label">
            SIDC-20 <span className="font-normal text-mist">(MIL-STD-2525E / APP-6D)</span>
          </label>
          <input
            id="unit-sidc"
            className="field font-mono text-[13px]"
            placeholder="10031000161211000000"
            value={values.sidc20}
            onChange={(e) => update('sidc20', e.target.value.replace(/\D/g, '').slice(0, 20))}
            inputMode="numeric"
            maxLength={20}
            autoComplete="off"
            spellCheck="false"
            disabled={busy}
          />
          <p className="mt-1 text-xs text-mist">
            20-digit symbol code rendered on the map. Default = friendly infantry battalion.
          </p>
          {errors.sidc20 && <p className="mt-1 text-xs text-rose-600">{errors.sidc20}</p>}
        </div>

        <div>
          <label htmlFor="unit-desc" className="field-label">
            Description <span className="font-normal text-mist">(optional)</span>
          </label>
          <textarea
            id="unit-desc"
            className="field min-h-[80px] resize-y"
            value={values.description}
            onChange={(e) => update('description', e.target.value)}
            maxLength={1024}
            disabled={busy}
          />
        </div>
      </form>
    </Modal>
  );
}
